import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import { Modal, Btn, INPUT, LABEL, localToday, r2, errMsg } from './ui';

// Return items against a bill: quantities per original line, priced and
// taxed exactly like the bill.
export default function DebitNoteModal({ bill, onClose, onSaved }) {
  const [returned, setReturned] = useState({});
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState('return');
  const [date, setDate] = useState(localToday());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const c = bill.currency;
  const balance = r2((bill.totalAmount || 0) - (bill.amountPaid || 0) - (bill.amountDebited || 0));

  useEffect(() => {
    api.get('/crm/purchases/debit-notes', { params: { billId: bill._id } }).then((res) => {
      const m = {};
      (res.data.debitNotes || []).filter((n) => n.status === 'issued').forEach((n) => n.lineItems.forEach((l) => { m[l.billLineId] = (m[l.billLineId] || 0) + l.quantity; }));
      setReturned(m);
    }).catch(() => {});
  }, [bill._id]);

  const left = (l) => r2(l.quantity - (returned[l._id] || 0));
  const estimate = r2(bill.lineItems.reduce((s, l) => {
    const q = Number(qty[l._id]) || 0;
    const per = l.quantity > 0 ? (l.netAmount + (bill.reverseCharge ? 0 : l.taxAmount)) / l.quantity : 0;
    return s + q * per;
  }, 0));

  const save = async () => {
    const lineItems = Object.entries(qty).map(([billLineId, q]) => ({ billLineId, quantity: Number(q) })).filter((x) => x.quantity > 0);
    setSaving(true);
    try {
      const { data } = await api.post('/crm/purchases/debit-notes', { billId: bill._id, lineItems, reason, date, notes });
      alertService.success('Debit note raised', `${data.debitNote.debitNoteNumber} for ${formatCurrency(data.debitNote.totalAmount, c)}`);
      onSaved();
    } catch (e) {
      alertService.error('Debit note not raised', errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Debit note · ${bill.billNumber}`} onClose={onClose} wide
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !(estimate > 0) || estimate > balance + 0.01} onClick={save}>{saving ? 'Saving…' : 'Raise debit note'}</Btn></>}>
      <p className="text-[13px] text-[#706E6B]">Still owed on this bill: <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(balance, c)}</strong>. Expense and input tax credit for the returned items are reversed in your books.</p>
      <div className="border border-[#D8DDE6] rounded overflow-x-auto">
        <table className="w-full text-[12px] sm:min-w-[35rem]">
          <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
            <th className="text-left font-semibold px-3 py-2">Item</th><th className="text-right font-semibold px-3 py-2">Billed</th>
            <th className="text-right font-semibold px-3 py-2">Can return</th><th className="text-right font-semibold px-3 py-2 w-32">Return now</th>
          </tr></thead>
          <tbody>
            {bill.lineItems.map((l) => (
              <tr key={l._id} className="border-t border-[#F3F2F2]">
                <td className="px-3 py-2">{l.description}<span className="block text-[11px] text-[#706E6B]">{formatCurrency(l.unitPrice, c)} · {l.taxRate}% tax</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                <td className="px-3 py-2 text-right tabular-nums">{left(l)}</td>
                <td className="px-3 py-1.5"><input aria-label={`Return quantity for ${l.description}`} type="number" min="0" max={left(l)} step="0.01" disabled={left(l) <= 0} className={cn(INPUT, 'text-right disabled:bg-[#F3F2F2]')} value={qty[l._id] || ''} onChange={(e) => setQty({ ...qty, [l._id]: e.target.value })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="dn-reason">Reason</label>
          <select id="dn-reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="return">Goods returned</option><option value="short_supply">Short supply</option><option value="price_correction">Price correction</option><option value="other">Other</option>
          </select>
        </div>
        <div><label className={LABEL} htmlFor="dn-date">Date</label><input id="dn-date" type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div><label className={LABEL} htmlFor="dn-notes">Notes</label><input id="dn-notes" className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <p className={cn('text-[13px]', estimate > balance + 0.01 ? 'text-[#BA0517]' : 'text-[#2E2E2E]')}>Reduces what you owe by about <strong className="tabular-nums">{formatCurrency(estimate, c)}</strong>.</p>
    </Modal>
  );
}
